// frontend/hooks/useSidebarData.ts

import { useState, useEffect, useRef } from "react";
import { signOut } from "next-auth/react";
import { API_URL, authOnlyHeaders } from "../utils/api";
import { loadFavorites } from "../utils/favorites";

export function useSidebarData(user: any, router: any, pathname: string, setTheme: any) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [colorTheme, setColorTheme] = useState("blue");
  // 유저별 테마 초기화를 한 번만 수행 (setTheme을 의존성에 넣으면 테마 변경 시 effect가 재실행되어 light로 초기화될 수 있음)
  const themeInitUserRef = useRef<string | null>(null);

  /** ACL 필터가 적용된 문서 목록을 서버에서 불러옵니다. */
  const fetchDocuments = async () => {
    if (!user) return;
    try {
      const res = await (async () => {
        try {
          return await fetch(`${API_URL}/documents`, {
            headers: authOnlyHeaders(user.accessToken),
          });
        } catch {
          return null;
        }
      })();

      if (!res) { setIsError(true); return; }
      if (res.status === 401) {
        // 계정 삭제 또는 토큰 만료 → 즉시 로그아웃
        signOut({ callbackUrl: '/login' });
        return;
      }
      if (!res.ok) { setIsError(true); return; }

      const data = await res.json();
      if (data) {
        // 즐겨찾기 정본이 서버(Favorite 테이블)로 옮겨졌다.
        // 이전에는 localStorage 만 읽어 다른 PC·다른 브라우저에서 즐겨찾기가 사라졌다.
        // loadFavorites 는 서버 조회 실패 시 마지막 캐시로 폴백하므로 목록이 비지 않는다.
        const favorites = await loadFavorites(user.id, user.accessToken);
        setDocuments(data.map((d: any) => ({ ...d, isFavorite: !!favorites[d.id] })));
        setIsError(false);
      }
    } catch (error) {
      console.error("문서 로딩 에러:", error);
      setIsError(true);
    }
  };

  // 유저별 색상·다크모드 테마 초기화 (유저가 바뀔 때만 실행)
  useEffect(() => {
    if (!user?.id || themeInitUserRef.current === user.id) return;
    themeInitUserRef.current = user.id;
    const savedColor = localStorage.getItem(`alpha-color-theme-${user.id}`);
    setColorTheme(savedColor || "blue");
    const savedMode = localStorage.getItem(`alpha-theme-mode-${user.id}`);
    // 저장된 사용자 설정이 있을 때만 next-themes를 덮어쓴다.
    // 값이 없는데 light를 강제하면 기존 모드가 되돌아간다.
    if (savedMode === "light" || savedMode === "dark" || savedMode === "system") {
      setTheme(savedMode);
    }
  }, [user?.id]); // setTheme을 의존성에서 제외 — next-themes 컨텍스트 업데이트 시 재실행되어 light로 덮어쓰는 현상 방지

  // 문서 로딩 및 전역 이벤트 구독
  useEffect(() => {
    fetchDocuments();

    const handleUpdate = () => fetchDocuments();
    const handleRoutePush = (e: any) => router.push(e.detail);
    // 페이지 상단바 즐겨찾기 버튼 클릭 시 사이드바 문서 목록 동기화
    const handleFavoriteChanged = (e: Event) => {
      const { docId, isFavorite } = (e as CustomEvent).detail;
      setDocuments(prev => prev.map((doc: any) => doc.id === docId ? { ...doc, isFavorite } : doc));
    };

    window.addEventListener("document-title-updated", handleUpdate);
    window.addEventListener("document-updated", handleUpdate);
    window.addEventListener("force-sidebar-refresh", handleUpdate);
    window.addEventListener("route-push", handleRoutePush);
    window.addEventListener("doc-favorite-changed", handleFavoriteChanged);

    return () => {
      window.removeEventListener("document-title-updated", handleUpdate);
      window.removeEventListener("document-updated", handleUpdate);
      window.removeEventListener("force-sidebar-refresh", handleUpdate);
      window.removeEventListener("route-push", handleRoutePush);
      window.removeEventListener("doc-favorite-changed", handleFavoriteChanged);
    };
  }, [user, router]);

  // URL 변경 감지하여 최근 조회 시간 즉시 갱신 (낙관적 업데이트)
  useEffect(() => {
    if (pathname && pathname.startsWith('/doc/')) {
      const currentDocId = pathname.replace('/doc/', '');
      setDocuments(prev => prev.map(doc =>
        doc.id === currentDocId ? { ...doc, viewedAt: new Date().toISOString() } : doc
      ));
    }
  }, [pathname]);

  // main-room(메인 페이지)은 최상단에 별도 고정 링크가 이미 있고, "최근 문서"·"전체 팀
  // 스페이스" 등 다른 목록을 통해 들어가면 /doc/[id] 라우트로 열려 main-room 전용 ADMIN
  // 편집 권한 로직(app/page.tsx)을 타지 않아 관리자가 수정할 수 없는 것처럼 보이는 문제가
  // 있었음 — 모든 파생 목록에서 제외.
  const teamRootDocs = documents.filter(d => d.workspaceType === "TEAM" && d.parentId === null && d.id !== "main-room");
  const personalRootDocs = documents.filter(d => d.workspaceType === "PERSONAL" && !d.isHandover && d.parentId === null);
  const handoverRootDocs = documents.filter(d => d.isHandover === true && d.parentId === null);

  const recentDocs = [...documents]
    .filter(d => d.viewedAt && d.id !== "main-room")
    .sort((a, b) => new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime())
    .slice(0, 5);

  const handleSetColorTheme = (color: string) => {
    setColorTheme(color);
    if (typeof window !== "undefined") {
      localStorage.setItem("alpha-color-theme-last", color);
      if (user?.id) localStorage.setItem(`alpha-color-theme-${user.id}`, color);
    }
  };

  return {
    documents, setDocuments, isError, colorTheme, setColorTheme: handleSetColorTheme, fetchDocuments,
    teamRootDocs, personalRootDocs, handoverRootDocs, recentDocs
  };
}